<script lang="ts" setup name="OrganisationPortCallsDetailsDocuments">
import { computed, reactive, type Ref, ref } from 'vue';

import { useSnackbarStore } from '~/store';

import { setDownloadAnchor } from '~/features/fileUtilities';
import { useReportFiles } from '~/features/useReportFiles';
import { useService } from '~/features/useService.js';
import { getKeyFromSearch } from '~/features/useUrlSearch';

import Service from '~/services/Service';

import BaseButton from '~/components/BaseButton.vue';
import CustomCard from '~/components/CustomCard.vue';
import DataTable from '~/components/DataTable.vue';
import DataTableActions from '~/components/DataTableActions.vue';
import DataTableLoading from '~/components/DataTableLoading.vue';
import PortCallsDetailsDocumentsTypeTag from '~/components/PortCallsDetailsDocumentsTypeTag.vue';
import SearchField from '~/components/SearchField.vue';
import ServiceCard from '~/components/ServiceCard.vue';

import type {
  IDataTableSettings,
  IFileDownloadResponse,
  IPortCallsDocumentsRow,
  PortCallsDocumentType,
  SortDirection,
} from '~/types';

const { portId, organisationId } = defineProps<{
  portId: string;
  organisationId: OrganisationId;
}>();

interface IQuery {
  search: string | null;
  sortBy: Partial<Record<'name' | 'type', SortDirection>>;
}

type DocumentsQueryType = Record<PortCallsDocumentType, (number | string)[]>;

const tableRows = ref<IPortCallsDocumentsRow[]>([]);

const onSearchClear = () => {
  query.search = '';
  query.sortBy = {};
};
const { previewReportFile, downloadReportFile } = useReportFiles();

const snackbarStore = useSnackbarStore();

const previewStatus = ref({});
const downloadStatus = ref({});
const isReportLoading = ref(false);
const selectedTableRows = ref<IPortCallsDocumentsRow[]>([]);

const hideSearch = computed(
  () => !serviceState.stateHasErrors && !serviceState.stateIsLoading,
);

const query = reactive<IQuery>({
  search: getKeyFromSearch('search'),
  sortBy: {},
});

const onSort = (key: string, direction: SortDirection) => {
  if (direction === null) {
    delete query.sortBy[key];
    return;
  }

  query.sortBy[key] = direction;
};

const tableSettings = computed<IDataTableSettings<IPortCallsDocumentsRow>>(
  () => ({
    editable: false,
    pagination: true,
    checkboxSelection: true,
    columns: [
      {
        header: 'Document Name',
        key: 'name',
        sortable: true,
        sortDirection: query.sortBy?.name,
      },
      {
        header: 'Document Type',
        key: 'type',
        sortable: true,
        sortDirection: query.sortBy?.type,
        render: () => PortCallsDetailsDocumentsTypeTag,
      },
    ],
    actions: (row?: IPortCallsDocumentsRow) => {
      const rowId = row?.id ?? '';
      const rowType = row?.type ?? '';

      return [
        {
          icon: previewStatus[rowId] ? 'mdi:loading' : 'ic:outline-preview',
          key: 'preview',
          dataTestId: 'report-preview',
          variant: 'btn-tertiary',
          tooltipDescription: 'View',
          handler: () => {
            previewReportFile(rowId, () =>
              fetchFile(rowId, rowType, previewStatus),
            );
          },
        },
        {
          icon: downloadStatus[rowId] ? 'mdi:loading' : 'mdi:download',
          key: 'download',
          dataTestId: 'report-download',
          variant: 'btn-tertiary',
          tooltipDescription: 'Download',
          handler: () => {
            downloadReportFile(rowId, () =>
              fetchFile(rowId, rowType, downloadStatus),
            );
          },
        },
      ];
    },
  }),
);

const serviceState = useService(
  Service.organisation(organisationId)
    .portCallsCalls()
    .documents(portId)
    .index(),
  ({ data }: { data: IPortCallsDocumentsRow[] }) => {
    if (!data.length) return false;
    tableRows.value = data;
    return true;
  },
  {
    loading: 'skeleton',
    cancelOnNewRequest: true,
    paginated: true,
    query: computed(() => ({
      search: query.search,
      sortBy: query.sortBy,
    })),
    getQuery: () => ({
      search: query.search,
      sortBy: query.sortBy,
    }),
  },
);

const onDownloadReport = () => {
  const documents: DocumentsQueryType = {
    bol: [],
    sof: [],
    nor: [],
    mates: [],
  };

  selectedTableRows.value.forEach(({ type, id }) => {
    documents[type].push(id!);
  });

  Service.organisation(organisationId)
    .portCallsCalls()
    .documents(portId)
    .download()
    .data({ documents })
    .onStart(() => (isReportLoading.value = true))
    .onSuccess((file: IFileDownloadResponse) => {
      setDownloadAnchor(file);

      snackbarStore.add({
        type: 'success',
        text: 'Downloaded successfully',
      });
    })
    .onError(() => {
      snackbarStore.add({
        type: 'error',
        text: 'Download failed',
      });
    })
    .onFinish(() => (isReportLoading.value = false))
    .execute();
};

const fetchFile = (
  id: string,
  type: PortCallsDocumentType | '',
  status: Ref,
): Promise<IFileDownloadResponse> => {
  return new Promise((resolve) => {
    Service.organisation(organisationId)
      .portCallsCalls()
      .documents(portId)
      .download()
      .data({
        documents: { [type]: [id] },
      })
      .onStart(() => {
        status.value[id] = true;
      })
      .onError(() => {
        status.value[id] = false;
      })
      .onSuccess((data: IFileDownloadResponse) => {
        status.value[id] = false;

        resolve(data);
      })
      .execute();
  });
};
</script>

<template>
  <CustomCard data-test="documents-table" title="Voyage Documents">
    <template #actions v-if="hideSearch">
      <BaseButton
        icon="mdi:download"
        variant="btn-secondary"
        :loading="isReportLoading"
        @click="onDownloadReport"
        :disabled="selectedTableRows.length === 0"
      >
        Download
      </BaseButton>
    </template>

    <div
      v-if="hideSearch"
      class="border-b border-t border-b-charcoal-6 border-t-charcoal-2"
    >
      <SearchField
        v-model="query.search"
        placeholder="Search by Document Name"
      />
    </div>

    <ServiceCard v-bind="serviceState" @clear="onSearchClear">
      <DataTableLoading
        v-if="serviceState.stateIsLoading"
        :columns="tableSettings.columns.length"
      />

      <DataTable
        v-else
        v-model:selectedTableRows="selectedTableRows"
        :settings="tableSettings"
        :data="tableRows"
        @sort="onSort"
      >
        <template #actions="{ item }">
          <DataTableActions :actions="tableSettings.actions!(item)" />
        </template>
      </DataTable>
    </ServiceCard>
  </CustomCard>
</template>
